import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import api, { formatApiError } from "@/lib/api";
import MessageBubble from "@/components/MessageBubble";
import Composer from "@/components/Composer";
import {
  Brain,
  Check,
  Copy,
  HelpCircle,
  Lightbulb,
  MessageCircle,
  Pencil,
  Share2,
  Sparkles,
  Workflow,
  X,
} from "lucide-react";
import { toast } from "sonner";

const MESSAGES_POLL_MS = 3000;

// Part 3 — message grouping & date dividers helpers.
// Group consecutive messages from the same sender if within 5 minutes.
const GROUPING_WINDOW_MS = 5 * 60 * 1000;

function sameDay(aIso, bIso) {
  try {
    const a = new Date(aIso);
    const b = new Date(bIso);
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  } catch {
    return false;
  }
}

function withinGroupingWindow(prev, curr) {
  if (!prev?.created_at || !curr?.created_at) return false;
  const a = new Date(prev.created_at).getTime();
  const b = new Date(curr.created_at).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return false;
  return Math.abs(b - a) <= GROUPING_WINDOW_MS;
}

function formatDayLabel(iso) {
  try {
    const d = new Date(iso);
    const now = new Date();
    const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    ).getTime();
    const startOfMsg = new Date(
      d.getFullYear(),
      d.getMonth(),
      d.getDate()
    ).getTime();
    const dayDiff = Math.round((startOfToday - startOfMsg) / 86400000);
    if (dayDiff === 0) return "Today";
    if (dayDiff === 1) return "Yesterday";
    if (dayDiff < 7 && dayDiff > 0) {
      return d.toLocaleDateString(undefined, { weekday: "long" });
    }
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year:
        d.getFullYear() === now.getFullYear() ? undefined : "numeric",
    });
  } catch {
    return "";
  }
}

// Part 4 — Empty-state artifact discovery prompt chips.
const EXAMPLE_PROMPTS = [
  {
    title: "Brainstorm",
    accent: "#D96C4A",
    Icon: Lightbulb,
    prompt:
      "Help us brainstorm 10 names for a calming meditation app aimed at busy parents.",
  },
  {
    title: "Draft together",
    accent: "#3A5A40",
    Icon: Pencil,
    prompt:
      "Draft a friendly two-paragraph announcement for our team that we're rolling out a new shared inbox next Monday.",
  },
  {
    title: "Plan a workflow",
    accent: "#7A4A2C",
    Icon: Workflow,
    prompt:
      "Outline a weekly content workflow we can both run: who does what each day, what we review together, and a checklist before publishing.",
  },
  {
    title: "Think it through",
    accent: "#2C2A29",
    Icon: Sparkles,
    prompt:
      "We're deciding between two pricing models for our SaaS. Walk us through the tradeoffs and ask any clarifying questions you need.",
  },
];

// Phase 4 — optimistic-send helpers
const PRIVATE_PREFIX_RE = /^\/private\s/i;
const MENTION_RE = /@([A-Za-z][A-Za-z0-9_]*)/g;
const newTempId = () =>
  `temp-${Math.random().toString(36).slice(2)}-${Date.now()}`;
const newThinkingId = () =>
  `thinking-${Math.random().toString(36).slice(2)}-${Date.now()}`;
const newErrorId = () =>
  `error-${Math.random().toString(36).slice(2)}-${Date.now()}`;

function isPrivateClient(content, knownUsers) {
  if (PRIVATE_PREFIX_RE.test(content)) return true;
  const lcNames = new Set(
    (knownUsers || []).map((u) => (u.display_name || "").toLowerCase())
  );
  if (lcNames.size === 0) return false;
  MENTION_RE.lastIndex = 0;
  let m;
  while ((m = MENTION_RE.exec(content)) !== null) {
    if (lcNames.has(m[1].toLowerCase())) {
      MENTION_RE.lastIndex = 0;
      return true;
    }
  }
  return false;
}

/** Merge a fresh server message list with locally-held optimistic items.
 *  - Drops local items whose id is in `serverList` (server now has them).
 *  - Drops `pending` temp user messages whose content+sender matches a recent
 *    (within 60s) server-confirmed user message — closes the race where the
 *    server has inserted the user msg under a real id but the POST response
 *    hasn't returned yet to replace our temp id.
 *  - Keeps everything else (optimistic + recently-resolved items not yet in
 *    this poll's response). */
function reconcileMessages(prev, serverList) {
  const serverIds = new Set(serverList.map((m) => m.id));
  const RECENT_MS = 60000;
  const now = Date.now();
  const recentServerUserKeys = new Set();
  for (const m of serverList) {
    if (m.sender_type !== "user") continue;
    const ts = new Date(m.created_at).getTime();
    if (!Number.isNaN(ts) && now - ts < RECENT_MS) {
      recentServerUserKeys.add(`${m.sender_email}|${m.content}`);
    }
  }
  const localOnly = prev.filter((m) => {
    if (serverIds.has(m.id)) return false;
    if (m.pending && m.sender_type === "user") {
      const key = `${m.sender_email}|${m.content}`;
      if (recentServerUserKeys.has(key)) return false;
    }
    return true;
  });
  return [...serverList, ...localOnly];
}

export default function ChatPane({
  workspaceId,
  workspace,
  chat,
  currentUser,
  otherUsers,
  onRenameChat,
  onChatActivity,
  onNewChat,
  onOpenSettings,
}) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [editingTitle, setEditingTitle] = useState(false);

  // Phase ChatPolish — empty-state prompt tile prefill
  const [prefillText, setPrefillText] = useState("");
  // Part 4 — How does this work? slide-over
  const [helpOpen, setHelpOpen] = useState(false);
  // Part 2 — auto-title suggestions chip
  const [titleSuggestions, setTitleSuggestions] = useState(null);
  const [suggestionsDismissed, setSuggestionsDismissed] = useState(false);
  const suggestionsFetchedRef = useRef(null);

  const scrollRef = useRef(null);
  const pollRef = useRef(null);
  const lastMessageCountRef = useRef(0);

  useEffect(() => {
    setTitleDraft(chat?.title || "");
    setEditingTitle(false);
  }, [chat?.id, chat?.title]);

  const fetchMessages = useCallback(
    async (chatId, { silent } = { silent: false }) => {
      if (!chatId || !workspaceId) return;
      try {
        const { data } = await api.get(
          `/workspaces/${workspaceId}/chats/${chatId}/messages`
        );
        const serverList = data || [];
        setMessages((prev) => reconcileMessages(prev, serverList));
      } catch (e) {
        if (!silent) toast.error(formatApiError(e));
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [workspaceId]
  );

  // Initial load when chat changes
  useEffect(() => {
    if (!chat?.id) {
      setMessages([]);
      return;
    }
    setLoading(true);
    fetchMessages(chat.id);
  }, [chat?.id, fetchMessages]);

  // Polling for active chat
  useEffect(() => {
    if (!chat?.id) return undefined;
    pollRef.current = setInterval(() => {
      fetchMessages(chat.id, { silent: true });
    }, MESSAGES_POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [chat?.id, fetchMessages]);

  // Auto-scroll to bottom when message count grows
  useLayoutEffect(() => {
    if (!scrollRef.current) return;
    if (messages.length !== lastMessageCountRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      lastMessageCountRef.current = messages.length;
    }
  }, [messages]);

  const handleSend = async (content, options = {}) => {
    if (!chat?.id || !workspaceId) return;
    const trimmed = (content || "").trim();
    const attIds = Array.isArray(options.attachment_ids)
      ? options.attachment_ids
      : [];
    const optimisticAtts = Array.isArray(options.optimistic_attachments)
      ? options.optimistic_attachments
      : [];
    // Allow attachment-only messages (text optional when images are present).
    if (!trimmed && attIds.length === 0) return;

    const isPrivate = isPrivateClient(content || "", otherUsers);
    const displayContent = isPrivate
      ? (content || "").replace(PRIVATE_PREFIX_RE, "")
      : (content || "");

    const tempId = newTempId();
    const thinkingId = isPrivate ? null : newThinkingId();
    const nowIso = new Date().toISOString();

    const optimisticUser = {
      id: tempId,
      chat_id: chat.id,
      sender_type: "user",
      sender_name: currentUser.display_name,
      sender_email: currentUser.email,
      content: displayContent,
      created_at: nowIso,
      excluded_from_ai: isPrivate,
      mentioned_user_ids: [],
      attachments: optimisticAtts,
      pending: true,
      _rawContent: content,
    };

    setMessages((prev) => {
      const next = [...prev, optimisticUser];
      if (thinkingId) {
        next.push({
          id: thinkingId,
          chat_id: chat.id,
          sender_type: "assistant",
          sender_name: "Claude Opus 4.8",
          content: "",
          created_at: nowIso,
          thinking: true,
        });
      }
      return next;
    });

    setSending(true);
    try {
      const { data } = await api.post(
        `/workspaces/${workspaceId}/chats/${chat.id}/messages`,
        { content: content || "", attachment_ids: attIds }
      );
      setMessages((prev) => {
        const replaced = prev.flatMap((m) => {
          if (m.id === tempId) {
            const out = [data.user_message];
            if (data.assistant_message && !thinkingId) {
              out.push(data.assistant_message);
            }
            return out;
          }
          if (thinkingId && m.id === thinkingId) {
            return data.assistant_message ? [data.assistant_message] : [];
          }
          return [m];
        });
        const seen = new Set();
        return replaced.filter((m) => {
          if (!m || !m.id) return false;
          if (seen.has(m.id)) return false;
          seen.add(m.id);
          return true;
        });
      });
      if (onChatActivity) onChatActivity();
    } catch (e) {
      setMessages((prev) =>
        prev.flatMap((m) => {
          if (m.id === tempId) {
            return [{ ...m, pending: false, error: true }];
          }
          if (thinkingId && m.id === thinkingId) {
            return [
              {
                id: newErrorId(),
                chat_id: chat.id,
                sender_type: "assistant",
                sender_name: "Claude Opus 4.8",
                content: "Claude couldn't respond. Try again.",
                created_at: new Date().toISOString(),
                error: true,
                _relatedTempId: tempId,
              },
            ];
          }
          return [m];
        })
      );
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const handleRetry = (failedMessage) => {
    if (!failedMessage) return;
    const raw = failedMessage._rawContent || failedMessage.content || "";
    setMessages((prev) =>
      prev.filter(
        (m) =>
          m.id !== failedMessage.id && m._relatedTempId !== failedMessage.id
      )
    );
    handleSend(raw);
  };

  // Phase ChatPolish — Part 2: auto-fetch title suggestions after the first
  // Claude reply lands. Triggers iff chat title is the default "New chat",
  // exactly 1 user msg + 1 assistant msg, suggestions not previously
  // dismissed in localStorage, not previously fetched this session.
  useEffect(() => {
    if (!chat?.id || !workspaceId) return;
    if (suggestionsFetchedRef.current === chat.id) return;
    if (suggestionsDismissed) return;
    if ((chat.title || "").trim().toLowerCase() !== "new chat") return;
    if (messages.length !== 2) return;
    const realMessages = messages.filter(
      (m) => !m.thinking && !m.pending && !m.error
    );
    if (realMessages.length !== 2) return;
    if (
      realMessages[0].sender_type !== "user" ||
      realMessages[1].sender_type !== "assistant"
    )
      return;
    try {
      if (
        typeof window !== "undefined" &&
        window.localStorage.getItem(`chat-title-dismissed:${chat.id}`)
      ) {
        return;
      }
    } catch {
      /* ignore */
    }
    suggestionsFetchedRef.current = chat.id;
    (async () => {
      try {
        const { data } = await api.post(
          `/workspaces/${workspaceId}/chats/${chat.id}/suggest-title`,
          {}
        );
        const list = Array.isArray(data?.suggestions) ? data.suggestions : [];
        if (list.length === 3) setTitleSuggestions(list);
      } catch {
        /* silent — feature is best-effort */
      }
    })();
  }, [chat?.id, chat?.title, workspaceId, messages, suggestionsDismissed]);

  // Reset suggestion state when switching chats
  useEffect(() => {
    setTitleSuggestions(null);
    setSuggestionsDismissed(false);
    suggestionsFetchedRef.current = null;
  }, [chat?.id]);

  const applyTitleSuggestion = useCallback(
    async (title) => {
      if (!chat?.id || !title) return;
      try {
        await onRenameChat(chat.id, title);
        toast.success(`Renamed to "${title}"`);
        setTitleSuggestions(null);
        setSuggestionsDismissed(true);
        try {
          window.localStorage.setItem(
            `chat-title-dismissed:${chat.id}`,
            "1"
          );
        } catch {
          /* ignore */
        }
      } catch (e) {
        toast.error(formatApiError(e));
      }
    },
    [chat?.id, onRenameChat]
  );

  const dismissTitleSuggestions = useCallback(() => {
    setTitleSuggestions(null);
    setSuggestionsDismissed(true);
    if (chat?.id) {
      try {
        window.localStorage.setItem(`chat-title-dismissed:${chat.id}`, "1");
      } catch {
        /* ignore */
      }
    }
  }, [chat?.id]);

  const commitTitle = () => {
    setEditingTitle(false);
    const next = titleDraft.trim();
    if (chat && next && next !== chat.title) {
      onRenameChat(chat.id, next);
    } else {
      setTitleDraft(chat?.title || "");
    }
  };

  if (!chat) {
    return (
      <main
        className="flex-1 flex flex-col bg-white h-full relative"
        data-testid="chat-pane-empty"
      >
        <Header
          title="Shared conversations"
          subtitle="Pick a thread or start a new one."
          showTitleEditing={false}
        />
        <div className="flex-1 flex items-center justify-center px-8 text-center">
          <div className="max-w-md">
            <div className="flex justify-center mb-6">
              <div className="w-14 h-14 rounded-full bg-[#F3F1EC] border border-[#DCD7CE] flex items-center justify-center">
                <MessageCircle
                  size={22}
                  className="text-[#D96C4A]"
                  strokeWidth={1.6}
                />
              </div>
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-[#2C2A29] mb-3">
              No conversation selected.
            </h2>
            <p className="font-sans text-base text-[#7A7571] leading-relaxed mb-6">
              Open a thread from the left, or begin a new shared one with
              Claude.
            </p>
            <button
              onClick={onNewChat}
              data-testid="empty-state-new-chat-button"
              className="px-5 py-2.5 rounded-lg bg-[#2C2A29] text-white font-sans text-sm font-medium hover:bg-[#1A1918] transition-colors"
            >
              Start a conversation
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main
      className="flex-1 flex flex-col bg-white h-full relative min-w-0"
      data-testid="chat-pane"
    >
      <Header
        showTitleEditing
        editingTitle={editingTitle}
        titleDraft={titleDraft}
        title={chat.title}
        subtitle={`Started by ${chat.created_by_name}`}
        onTitleDraftChange={setTitleDraft}
        onStartEditTitle={() => setEditingTitle(true)}
        onCommitTitle={commitTitle}
        memoryOn={!!workspace?.project_memory_enabled}
        onOpenSettings={onOpenSettings}
        onOpenHelp={() => setHelpOpen(true)}
        workspaceId={workspace?.id}
        workspaceName={workspace?.name}
        chatId={chat.id}
      />

      {/* Phase ChatPolish — Part 2: auto-title suggestion chip */}
      {titleSuggestions && titleSuggestions.length === 3 ? (
        <div
          className="px-6 md:px-10 py-3 border-b border-[#DCD7CE] bg-[#F8F6F1]"
          data-testid="title-suggestions-chip"
        >
          <div className="max-w-3xl mx-auto flex items-center gap-3 flex-wrap">
            <span className="font-sans text-[11px] uppercase tracking-[0.18em] text-[#7A7571] font-semibold">
              Suggestions for this chat&rsquo;s title
            </span>
            {titleSuggestions.map((s, i) => (
              <button
                key={i}
                onClick={() => applyTitleSuggestion(s)}
                data-testid={`title-suggestion-${i}`}
                className="px-3 py-1.5 rounded-full bg-white border border-[#DCD7CE] text-[#2C2A29] font-sans text-xs hover:border-[#D96C4A] hover:bg-[#FFF6F0] transition-colors"
              >
                {s}
              </button>
            ))}
            <button
              onClick={dismissTitleSuggestions}
              data-testid="title-suggestion-keep-current"
              className="px-3 py-1.5 rounded-full text-[#7A7571] font-sans text-xs hover:text-[#2C2A29] hover:bg-white transition-colors ml-auto"
            >
              Keep &ldquo;{chat.title || "current"}&rdquo;
            </button>
          </div>
        </div>
      ) : null}

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-6 md:px-10 pt-8 pb-6"
        data-testid="messages-scroll"
      >
        <div className="max-w-3xl mx-auto w-full">
          {loading ? (
            <p className="font-sans text-sm text-[#7A7571] text-center py-12">
              Loading messages…
            </p>
          ) : messages.length === 0 ? (
            <div className="py-12" data-testid="messages-empty">
              <div className="flex justify-center mb-4">
                <Sparkles
                  size={20}
                  className="text-[#D96C4A]"
                  strokeWidth={1.6}
                />
              </div>
              <h3 className="font-serif text-3xl text-[#2C2A29] text-center mb-2">
                Start the conversation.
              </h3>
              <p className="font-sans text-sm text-[#7A7571] text-center mb-8 max-w-md mx-auto">
                Anything you write will be visible to everyone in this
                workspace — and to Claude. Or try one of these:
              </p>
              <div
                className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                data-testid="empty-state-prompts"
              >
                {EXAMPLE_PROMPTS.map((p, i) => {
                  const I = p.Icon;
                  return (
                    <button
                      key={i}
                      onClick={() => setPrefillText(p.prompt)}
                      data-testid={`empty-state-prompt-${i}`}
                      className="text-left p-4 rounded-xl border border-[#DCD7CE] bg-white hover:border-[#D96C4A] hover:bg-[#FFF6F0] transition-colors group"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <I
                          size={14}
                          strokeWidth={1.8}
                          style={{ color: p.accent }}
                        />
                        <span className="font-sans text-[11px] uppercase tracking-[0.18em] font-semibold text-[#7A7571] group-hover:text-[#2C2A29]">
                          {p.title}
                        </span>
                      </div>
                      <p className="font-sans text-sm text-[#2C2A29] leading-relaxed line-clamp-3">
                        {p.prompt}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            messages.map((m, i) => {
              const prev = i > 0 ? messages[i - 1] : null;
              const next = i < messages.length - 1 ? messages[i + 1] : null;
              const dayChanged =
                !prev ||
                (m.created_at &&
                  prev.created_at &&
                  !sameDay(prev.created_at, m.created_at));
              const sameAsPrev =
                !dayChanged &&
                prev &&
                prev.sender_type === m.sender_type &&
                (prev.sender_name || "") === (m.sender_name || "") &&
                withinGroupingWindow(prev, m);
              const sameAsNext =
                next &&
                m.created_at &&
                next.created_at &&
                sameDay(m.created_at, next.created_at) &&
                next.sender_type === m.sender_type &&
                (next.sender_name || "") === (m.sender_name || "") &&
                withinGroupingWindow(m, next);
              return (
                <React.Fragment key={m.id}>
                  {dayChanged && m.created_at ? (
                    <div
                      className="flex items-center gap-3 my-6"
                      data-testid="day-divider"
                    >
                      <div className="flex-1 h-px bg-[#DCD7CE]" />
                      <span className="font-sans text-[10px] uppercase tracking-[0.22em] text-[#A5A29E] font-semibold">
                        {formatDayLabel(m.created_at)}
                      </span>
                      <div className="flex-1 h-px bg-[#DCD7CE]" />
                    </div>
                  ) : null}
                  <MessageBubble
                    message={m}
                    currentUser={currentUser}
                    onRetry={handleRetry}
                    showSender={!sameAsPrev}
                    showTimestamp={!sameAsNext}
                  />
                </React.Fragment>
              );
            })
          )}
        </div>
      </div>

      <div className="px-6 md:px-10 pb-6 pt-2 bg-gradient-to-t from-white via-white to-transparent">
        <Composer
          workspaceId={workspaceId}
          currentUser={currentUser}
          otherUsers={otherUsers}
          sending={sending}
          onSend={handleSend}
          prefillText={prefillText}
          onPrefillConsumed={() => setPrefillText("")}
          trialExhausted={
            workspace?.plan === "free" &&
            Number(workspace?.trial_anthropic_cents_remaining || 0) <= 0
          }
        />
      </div>
      {helpOpen ? <HelpPanel onClose={() => setHelpOpen(false)} /> : null}
    </main>
  );
}

function Header({
  title,
  subtitle,
  showTitleEditing,
  editingTitle,
  titleDraft,
  onTitleDraftChange,
  onStartEditTitle,
  onCommitTitle,
  memoryOn,
  onOpenSettings,
  onOpenHelp,
  workspaceId,
  workspaceName,
  chatId,
}) {
  return (
    <header
      className="flex items-center justify-between gap-4 px-6 md:px-10 py-5 border-b border-[#DCD7CE] bg-white/85 backdrop-blur-md sticky top-0 z-10"
      data-testid="chat-header"
    >
      <div className="flex flex-col min-w-0 flex-1">
        {showTitleEditing ? (
          editingTitle ? (
            <input
              autoFocus
              value={titleDraft}
              onChange={(e) => onTitleDraftChange(e.target.value)}
              onBlur={onCommitTitle}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onCommitTitle();
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  onCommitTitle();
                }
              }}
              data-testid="chat-title-input"
              className="font-serif text-2xl sm:text-3xl tracking-tight text-[#2C2A29] bg-transparent border-b border-[#D96C4A]/40 focus:outline-none focus:border-[#D96C4A] w-full max-w-[28rem]"
            />
          ) : (
            <button
              onClick={onStartEditTitle}
              data-testid="chat-title-button"
              className="font-serif text-2xl sm:text-3xl tracking-tight text-[#2C2A29] text-left hover:opacity-80 transition-opacity max-w-full truncate"
              title="Click to rename"
            >
              {title || "Untitled"}
            </button>
          )
        ) : (
          <h1 className="font-serif text-2xl sm:text-3xl tracking-tight text-[#2C2A29]">
            {title}
          </h1>
        )}
        {subtitle ? (
          <p className="font-sans text-xs uppercase tracking-[0.18em] text-[#7A7571] mt-1 truncate">
            {subtitle}
          </p>
        ) : null}
      </div>
      <div className="shrink-0 flex items-center gap-2">
        {onOpenHelp ? (
          <button
            type="button"
            onClick={onOpenHelp}
            data-testid="chat-header-help-button"
            title="How does this work?"
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-[#DCD7CE] text-[#7A7571] hover:bg-[#F3F1EC] hover:text-[#2C2A29] transition-colors"
          >
            <HelpCircle size={13} strokeWidth={2} />
            <span className="font-sans text-[11px] font-medium tracking-tight hidden sm:inline">
              How does this work?
            </span>
          </button>
        ) : null}
        {workspaceId && chatId ? (
          <ShareChatButton
            workspaceId={workspaceId}
            workspaceName={workspaceName}
            chatId={chatId}
          />
        ) : null}
        {memoryOn ? (
          <button
            type="button"
            onClick={onOpenSettings}
            data-testid="chat-header-project-memory-badge"
            title="Project memory is on. Click to manage."
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-[#7A4A2C]/10 border border-[#7A4A2C]/25 text-[#7A4A2C] hover:bg-[#7A4A2C]/15 transition-colors"
          >
            <Brain size={13} strokeWidth={2} />
            <span className="font-sans text-[11px] font-medium tracking-tight">
              Project memory on
            </span>
          </button>
        ) : null}
      </div>
    </header>
  );
}


// Phase Share-chat — small share popover anchored to the chat header button.
function ShareChatButton({ workspaceId, workspaceName, chatId }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const popRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const ensureUrl = useCallback(async () => {
    if (shareUrl) return;
    setLoading(true);
    try {
      const { data } = await api.post(
        `/workspaces/${workspaceId}/chats/${chatId}/share`,
        {}
      );
      setShareUrl(data?.share_url || "");
    } catch (e) {
      toast.error(formatApiError(e));
      setOpen(false);
    } finally {
      setLoading(false);
    }
  }, [shareUrl, workspaceId, chatId]);

  const openPopover = async () => {
    setOpen(true);
    await ensureUrl();
  };

  const copy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Share link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy. Long-press to copy manually.");
    }
  };

  return (
    <div className="relative" ref={popRef}>
      <button
        type="button"
        onClick={openPopover}
        data-testid="chat-header-share-button"
        title="Share this chat — generates an invite link."
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-[#DCD7CE] text-[#2C2A29] hover:bg-[#F3F1EC] transition-colors"
      >
        <Share2 size={13} strokeWidth={2} />
        <span className="font-sans text-[11px] font-medium tracking-tight">
          Share
        </span>
      </button>
      {open ? (
        <div
          data-testid="share-chat-popover"
          className="absolute right-0 top-full mt-2 w-[22rem] max-w-[calc(100vw-2rem)] z-40 rounded-xl border border-[#DCD7CE] bg-white shadow-xl px-5 py-4"
        >
          <h3 className="font-serif text-lg text-[#2C2A29] mb-1">
            Share this chat
          </h3>
          <p className="font-sans text-xs text-[#7A7571] leading-relaxed mb-3">
            Anyone with this link can join{" "}
            <strong className="text-[#2C2A29]">
              {workspaceName || "this workspace"}
            </strong>{" "}
            as a Member and open this chat.
          </p>
          <div className="flex items-stretch gap-2 mb-2">
            <input
              type="text"
              readOnly
              value={loading ? "Generating link…" : shareUrl}
              onFocus={(e) => e.target.select()}
              data-testid="share-chat-url-input"
              className="flex-1 min-w-0 px-2.5 py-2 rounded-md border border-[#DCD7CE] bg-[#F8F6F1] font-mono text-[11px] text-[#2C2A29] focus:outline-none focus:border-[#D96C4A]"
            />
            <button
              type="button"
              onClick={copy}
              disabled={loading || !shareUrl}
              data-testid="share-chat-copy-button"
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#2C2A29] text-white font-sans text-xs font-medium hover:bg-[#1A1918] disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {copied ? (
                <>
                  <Check size={12} strokeWidth={2} />
                  Copied
                </>
              ) : (
                <>
                  <Copy size={12} strokeWidth={2} />
                  Copy
                </>
              )}
            </button>
          </div>
          <p className="font-sans text-[10px] text-[#A5A29E] leading-relaxed">
            This link is also added to your workspace&rsquo;s active invites
            (Settings &rarr; Members &rarr; Active invites) and can be revoked
            there.
          </p>
        </div>
      ) : null}
    </div>
  );
}

// Part 4 — "How does this work?" slide-over panel.
// Plain, friendly explanation of the shared-chat model + Claude attribution.
function HelpPanel({ onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex"
      data-testid="help-panel-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="help-panel-title"
    >
      <button
        type="button"
        aria-label="Close help"
        onClick={onClose}
        data-testid="help-panel-scrim"
        className="flex-1 bg-[#2C2A29]/40 backdrop-blur-[2px]"
      />
      <aside
        className="w-full max-w-md h-full bg-white shadow-2xl border-l border-[#DCD7CE] flex flex-col"
        data-testid="help-panel"
      >
        <header className="flex items-center justify-between px-6 py-5 border-b border-[#DCD7CE]">
          <div>
            <p className="font-sans text-[10px] uppercase tracking-[0.22em] text-[#7A7571] font-semibold mb-1">
              About this workspace
            </p>
            <h2
              id="help-panel-title"
              className="font-serif text-2xl text-[#2C2A29]"
            >
              How does this work?
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="help-panel-close"
            className="w-9 h-9 rounded-full flex items-center justify-center text-[#7A7571] hover:bg-[#F3F1EC] hover:text-[#2C2A29] transition-colors"
            title="Close (Esc)"
          >
            <X size={18} strokeWidth={1.8} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          <section>
            <h3 className="font-sans text-[11px] uppercase tracking-[0.18em] text-[#7A7571] font-semibold mb-2">
              Everyone&rsquo;s in the room
            </h3>
            <p className="font-sans text-sm text-[#2C2A29] leading-relaxed">
              This is a shared chat — every member of the workspace sees the
              same threads. Your messages are labeled with your name, your
              teammate&rsquo;s with theirs, and Claude is labeled{" "}
              <strong>Claude Opus 4.8</strong>. New messages appear within a
              few seconds without refreshing.
            </p>
          </section>

          <section>
            <h3 className="font-sans text-[11px] uppercase tracking-[0.18em] text-[#7A7571] font-semibold mb-2">
              When Claude replies
            </h3>
            <p className="font-sans text-sm text-[#2C2A29] leading-relaxed">
              Send a message and Claude responds in the same thread. Claude
              reads the full conversation, so context from both of you carries
              forward.
            </p>
          </section>

          <section>
            <h3 className="font-sans text-[11px] uppercase tracking-[0.18em] text-[#7A7571] font-semibold mb-2">
              Side conversations
            </h3>
            <p className="font-sans text-sm text-[#2C2A29] leading-relaxed">
              Start a message with <code className="font-mono text-[12px] bg-[#F3F1EC] px-1 py-0.5 rounded">/private</code>{" "}
              or mention a teammate by name to keep that line out of
              Claude&rsquo;s context (it&rsquo;s still visible to everyone in
              the workspace, just not the assistant).
            </p>
          </section>

          <section>
            <h3 className="font-sans text-[11px] uppercase tracking-[0.18em] text-[#7A7571] font-semibold mb-2">
              Share a chat
            </h3>
            <p className="font-sans text-sm text-[#2C2A29] leading-relaxed">
              Tap <strong>Share</strong> in the chat header to generate an
              invite link that brings someone straight into this thread. You
              can revoke any invite from Settings &rarr; Members.
            </p>
          </section>

          <section>
            <h3 className="font-sans text-[11px] uppercase tracking-[0.18em] text-[#7A7571] font-semibold mb-2">
              Keyboard
            </h3>
            <ul className="font-sans text-sm text-[#2C2A29] leading-relaxed space-y-1.5">
              <li>
                <kbd className="font-mono text-[11px] bg-[#F3F1EC] border border-[#DCD7CE] px-1.5 py-0.5 rounded">
                  Enter
                </kbd>{" "}
                — Send
              </li>
              <li>
                <kbd className="font-mono text-[11px] bg-[#F3F1EC] border border-[#DCD7CE] px-1.5 py-0.5 rounded">
                  Shift
                </kbd>{" "}
                +{" "}
                <kbd className="font-mono text-[11px] bg-[#F3F1EC] border border-[#DCD7CE] px-1.5 py-0.5 rounded">
                  Enter
                </kbd>{" "}
                — New line
              </li>
              <li>
                <kbd className="font-mono text-[11px] bg-[#F3F1EC] border border-[#DCD7CE] px-1.5 py-0.5 rounded">
                  Esc
                </kbd>{" "}
                — Close this panel
              </li>
            </ul>
          </section>
        </div>
        <footer className="px-6 py-4 border-t border-[#DCD7CE] bg-[#F8F6F1]">
          <p className="font-sans text-[11px] text-[#7A7571] leading-relaxed">
            Questions or feedback? Reach out through Settings &rarr; Support.
          </p>
        </footer>
      </aside>
    </div>
  );
}
